import { apiService } from './api';
import { ApiResponse, ChatSession, ExcelData, ChartData, ChatMessage } from '../types';
interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}
export class ChatService {
  /**
   * Send chat query with streaming (agent chat only)
   * Returns EventSource for Server-Sent Events
   */
  async sendQueryStream(
    query: string,
    workspaceId: string,
    sessionId: string | undefined,
    onToken: (token: string) => void,
    onStep: (step: { tool: string; status: string; toolInput?: string; toolOutput?: string; error?: string }) => void,
    onComplete: (data: {
      chartUrl?: string;
      chartData?: ChartData;
      excelData?: ExcelData;
      sql?: string;
      lastSyncTimestamp?: string;
      executionTimeMs: number;
      tokenUsage?: {
        promptTokens: number;
        completionTokens: number;
        totalTokens: number;
      };
      finishReason?: string;
    }) => void,
    onError: (error: string) => void,
    onChartData?: (chartData: { rows: (string | number | boolean | null)[][]; columns: string[]; rowCount: number }) => void
  ): Promise<string> {
    // Import dynamically to avoid circular dependency
    const { useAuthStore } = await import('../store/authStore');
    const authToken = useAuthStore.getState().token;

    if (!authToken) {
      onError('No authentication token available');
      throw new Error('No authentication token available');
    }

    const apiUrl = import.meta.env.VITE_API_URL;

    // Extract base URL and construct full streaming endpoint
    const streamUrl = `${apiUrl}/agent-chat/query-stream`;

    return new Promise((resolve, reject) => {
      fetch(streamUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ query, workspaceId, sessionId }),
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
            onError(errorData.message || 'Request failed');
            reject(new Error(errorData.message || 'Request failed'));
            return;
          }

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();
          let sessionIdResult = sessionId || '';

          if (!reader) {
            onError('No response body');
            reject(new Error('No response body'));
            return;
          }

          const processStream = async () => {
            try {
              // eslint-disable-next-line no-constant-condition
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n\n');

                for (const line of lines) {
                  if (line.startsWith('data: ')) {
                    try {
                      const jsonData = JSON.parse(line.substring(6));

                      if (jsonData.type === 'session') {
                        sessionIdResult = jsonData.sessionId;
                      } else if (jsonData.type === 'token') {
                        onToken(jsonData.content);
                      } else if (jsonData.type === 'step') {
                        onStep({
                          tool: jsonData.tool,
                          status: jsonData.status || 'running',
                          toolInput: jsonData.toolInput,
                          toolOutput: jsonData.toolOutput,
                          error: jsonData.error
                        });
                      } else if (jsonData.type === 'chartData') {
                        // Chart data from query results - send immediately for rendering
                        if (onChartData) {
                          onChartData(jsonData.chartData);
                        }
                      } else if (jsonData.type === 'complete') {
                        onComplete({
                          chartUrl: jsonData.chartUrl,
                          chartData: jsonData.chartData,
                          excelData: jsonData.excelData,
                          sql: jsonData.sql,
                          lastSyncTimestamp: jsonData.lastSyncTimestamp,
                          executionTimeMs: jsonData.executionTimeMs,
                          tokenUsage: jsonData.tokenUsage,
                          finishReason: jsonData.finishReason,
                        });
                        resolve(sessionIdResult);
                      } else if (jsonData.type === 'error') {
                        const errorMessage = jsonData.error || jsonData.message || 'An unknown error occurred';
                        onError(errorMessage);
                        reject(new Error(errorMessage));
                      }
                    } catch (e) {
                      console.warn('Failed to parse SSE message:', line);
                    }
                  }
                }
              }
            } catch (error) {
              const apiError = error as ApiError;
              onError(apiError.message);
              reject(apiError);
            }
          };

          processStream();
        })
        .catch((error) => {
          onError(error.message);
          reject(error);
        });
    });
  }

  /**
   * Enhance a user's prompt via backend LLM helper
   */
  async enhancePrompt(
    prompt: string,
    workspaceId: string
  ): Promise<ApiResponse<{ enhancedPrompt: string }>> {
    return await apiService.post('/agent-chat/prompt/enhance', { prompt, workspaceId });
  }

  /**
   * Get session by ID
   */
  async getSession(sessionId: string): Promise<ApiResponse<ChatSession>> {
    return await apiService.get(`/agent-chat/sessions/${sessionId}`);
  }

  /**
   * Get session messages
   */
  async getSessionMessages(sessionId: string): Promise<ApiResponse<ChatMessage[]>> {
    return await apiService.get(`/agent-chat/sessions/${sessionId}/messages`);
  }

  /**
   * Get session charts
   */
  async getSessionCharts(sessionId: string): Promise<ApiResponse<Array<{ messageId: string; chartUrl: string; timestamp: string }>>> {
    return await apiService.get(`/agent-chat/sessions/${sessionId}/charts`);
  }

  /**
   * List user sessions (metadata only)
   */
  async listSessions(workspaceId?: string, page: number = 1, limit: number = 20): Promise<ApiResponse<ChatSession[]>> {
    const params = new URLSearchParams();
    if (workspaceId) params.append('workspaceId', workspaceId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    return await apiService.get(`/agent-chat/sessions?${params.toString()}`);
  }

  /**
   * Create new session
   */
  async createSession(workspaceId: string): Promise<ApiResponse<ChatSession>> {
    return await apiService.post('/agent-chat/sessions', { workspaceId });
  }

  /**
   * Delete session
   */
  async deleteSession(sessionId: string): Promise<ApiResponse<void>> {
    return await apiService.delete(`/agent-chat/sessions/${sessionId}`);
  }
}

export const chatService = new ChatService();
